import { ErrorComponent } from './../components/error/error.component';
import { Injectable, inject } from '@angular/core';
import { AlertController, NavController, ModalController } from '@ionic/angular';
import { Observable, from } from 'rxjs';
import { flatMap } from 'rxjs/operators';
import { StateError } from '../state';
import { Haptics, NotificationType } from '@capacitor/haptics';
import { AuthFacade } from '../../auth/state/auth.facade';

@Injectable()
export class AlertService {

	// A field rather than a constructor parameter: every dialog service extends this one with its own constructor.
	private authFacade = inject(AuthFacade);

	public constructor(protected alertCtrl: AlertController, protected navCtrl: NavController, private modalCtrl: ModalController) {

	}

	protected showAsObservable(input: Promise<HTMLIonAlertElement>) {
		return from(input).pipe(flatMap(x => from(x.present())));
	}

	protected show(input: Promise<HTMLIonAlertElement>) {
		const sub = from(input).subscribe(alert => {
			alert.present();
			sub.unsubscribe();
		});
	}

	usersOnlyAlert(functionalityName: string) {
		const alert = this.alertCtrl.create({
			header: 'Oeps!',
			message: functionalityName + ' is alleen beschikbaar voor ingelogde gebruikers.',
			buttons: [
				{
					text: 'Inloggen',
					handler: () => {
						this.authFacade.login();
					}
				},
				{
					text: 'Registreren',
					handler: () => {
						// ID-6B: registering happens at Pro Pool ID.
						this.authFacade.register();
					}
				},
				{
					text: 'Annuleren',
					role: 'cancel',
					handler: () => {
					}
				}
			]
		});
		this.show(alert);
	}

	nativeAndroidFunctionNotAvailable(): Observable<void> {
		const alert = this.alertCtrl.create({
			message: 'Oeps!',
			subHeader: 'Deze functionaliteit is niet beschikbaar. Download de app vanuit de App Store om deze functionaliteit te gebruiken.',
			buttons: [
				{
					text: 'OK',
					role: 'cancel',
					handler: () => {}
				},
				{
					text: 'Naar App Store',
					handler: () => {
						window.location.href = 'https://play.google.com/store/apps/details?id=com.propool.Propoolapp';
					}
				}
			]
		});
		return this.showAsObservable(alert);
	}

	nativeIosFunctionNotAvailable(): Observable<void> {
		const alert = this.alertCtrl.create({
			message: 'Oeps!',
			subHeader: 'Deze functionaliteit is niet beschikbaar. Download de app vanuit de App Store om deze functionaliteit te gebruiken.',
			buttons: [
				{
					text: 'OK',
					role: 'cancel',
					handler: () => {
					}
				},
				{
					text: 'Naar App Store',
					handler: () => {
						window.location.href = 'https://itunes.apple.com/nl/app/pro-pool/id1271217711?mt=8';
					}
				}
			]
		});
		return this.showAsObservable(alert);
	}

	showConnectionError(): void {
		// const alert = this.alertCtrl.create({
		// 	header: 'Error!',
		// 	subHeader: 'Er kon geen verbinding gemaakt worden met de ProPool server. Controleer je internetverbinding',
		// 	buttons: ['OK']
		// });
		// return this.show(alert);
		Haptics.notification({ type: NotificationType.Warning });
		this.modalCtrl.create({
			component: ErrorComponent,
			cssClass: 'error-backdrop',
			showBackdrop: true,
			backdropDismiss: true,
			htmlAttributes: { 'aria-label': 'Fout' },
			componentProps: {
				error: StateError.NoInternetAccess
			}
		}).then(modal => modal.present());
	}

	// For 4xx (other than 400, which components/effects handle with their own targeted messaging - see
	// httperror.interceptor.ts) and 5xx responses: the server was reached and responded, so telling the
	// user to check their internet connection (as showConnectionError does) is actively wrong.
	showServerError(): void {
		Haptics.notification({ type: NotificationType.Warning });
		this.modalCtrl.create({
			component: ErrorComponent,
			cssClass: 'error-backdrop',
			showBackdrop: true,
			backdropDismiss: true,
			htmlAttributes: { 'aria-label': 'Fout' },
			componentProps: {
				error: StateError.ServerError
			}
		}).then(modal => modal.present());
	}

	// Used to guard navigating/dismissing away from a form with unsaved changes (canDismiss on a
	// modal, or a CanDeactivate route guard on a routed page) - resolves true if it's OK to leave.
	async confirmDiscardChanges(): Promise<boolean> {
		const alert = await this.alertCtrl.create({
			header: 'Wijzigingen weggooien?',
			message: 'Je hebt niet-opgeslagen wijzigingen. Weet je zeker dat je wilt sluiten?',
			buttons: [
				{ text: 'Annuleren', role: 'cancel' },
				{ text: 'Weggooien', role: 'destructive' }
			]
		});
		await alert.present();
		const { role } = await alert.onDidDismiss();
		return role === 'destructive';
	}

	globalErrorAlert() {
		const alert = this.alertCtrl.create({
			header: 'Oeps!',
			message: 'Er is iets fout gegaan.',
			buttons: ['Opnieuw proberen']
		});
		this.show(alert);
	}

	checkoutSuccessAlert(): void {
		const alert = this.alertCtrl.create({
			header: 'Gelukt!',
			message: 'Je abonnement is geactiveerd.',
			buttons: ['OK']
		});
		this.show(alert);
	}

	checkoutCancelledAlert(): void {
		const alert = this.alertCtrl.create({
			header: 'Geannuleerd',
			message: 'Je hebt de betaling geannuleerd. Er is niets afgeschreven.',
			buttons: ['OK']
		});
		this.show(alert);
	}

	subscriptionRequiredAlert(planName: string): void {
		const alert = this.alertCtrl.create({
			header: 'Abonnement vereist',
			message: `Hiervoor heb je het abonnement "${planName}" nodig.`,
			buttons: [
				{ text: 'Annuleren', role: 'cancel' },
				{
					text: 'Bekijk abonnementen',
					handler: () => {
						this.navCtrl.navigateForward('/tabs/profile/subscriptions');
					}
				}
			]
		});
		this.show(alert);
	}

	emptyNameAlert() {
		const alert = this.alertCtrl.create({
			header: 'Oeps!',
			message: 'Je hebt geen naam opgegeven.',
			buttons: ['Opnieuw proberen']
		})
		this.show(alert);
	}

	emptyDescriptionAlert() {
		const alert = this.alertCtrl.create({
			message: 'Oeps!',
			subHeader: 'Je hebt geen beschrijving opgegeven.',
			buttons: ['Opnieuw proberen']
		});
		this.show(alert);
	}

	emptyEmailAlert() {
		const alert = this.alertCtrl.create({
			header: 'Oeps!',
			message: 'Je hebt geen e-mailadres opgegeven.',
			buttons: ['Opnieuw proberen']
		});
		this.show(alert);
	}

	wrongEmailAlert() {
		const alert = this.alertCtrl.create({
			header: 'Oeps!',
			message: 'Je hebt een onjuist e-mailadres opgegeven.',
			buttons: ['Opnieuw proberen']
		});
		this.show(alert);
	}
}
